import React, { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, FormControl, FormControlLabel, InputAdornment,
  MenuItem, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { CLP } from '../utils/finance';
import { auth } from '../firebase';
import {
  createHonorarioAjuste,
  deleteHonorarioAjuste,
  getHonorarioAjustes,
  updateHonorarioAjuste,
} from '../services/honorarioService';

const TIPOS = [
  { value: 'adelanto', label: 'Adelanto' },
  { value: 'retencion', label: 'Retención' },
  { value: 'nota_credito', label: 'Nota de crédito' },
  { value: 'clawback', label: 'Clawback / devolución' },
  { value: 'bono', label: 'Bono' },
  { value: 'otro', label: 'Otro' },
];

export default function HonorariosAjustesTab({ professionals }) {
  const [ajustes, setAjustes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedProf, setSelectedProf] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    tipo: 'otro',
    monto: '',
    descripcion: '',
    fecha: new Date().toISOString().split('T')[0],
    pagoConfirmado: false,
    paciente: '',
  });

  const load = async (pid) => {
    if (!pid) return;
    setLoading(true);
    try {
      const data = await getHonorarioAjustes(pid);
      setAjustes(data.filter((a) => a.status !== 'eliminado'));
    } catch (e) {
      console.error('Error cargando ajustes:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(selectedProf);
  }, [selectedProf]);

  const resetForm = () => {
    setEditing(null);
    setForm({
      tipo: 'otro',
      monto: '',
      descripcion: '',
      fecha: new Date().toISOString().split('T')[0],
      pagoConfirmado: false,
      paciente: '',
    });
  };

  const handleEdit = (a) => {
    setEditing(a);
    setForm({
      tipo: a.tipo || 'otro',
      monto: a.monto || '',
      descripcion: a.descripcion || '',
      fecha: a.fecha || new Date().toISOString().split('T')[0],
      pagoConfirmado: a.pagoConfirmado !== false,
      paciente: a.paciente || '',
    });
  };

  const handleSave = async () => {
    if (!selectedProf || !form.monto) return;
    setSaving(true);
    try {
      const payload = {
        professionalId: selectedProf,
        tipo: form.tipo,
        monto: Number(form.monto),
        descripcion: form.descripcion,
        fecha: form.fecha,
        pagoConfirmado: form.pagoConfirmado,
        paciente: form.paciente,
      };
      if (editing) {
        await updateHonorarioAjuste(editing.id, payload);
      } else {
        await createHonorarioAjuste(payload);
      }
      resetForm();
      await load(selectedProf);
    } catch (e) {
      console.error('Error guardando ajuste:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteHonorarioAjuste(id, { userId: auth.currentUser?.uid || '' });
      await load(selectedProf);
    } catch (e) {
      console.error('Error eliminando ajuste:', e);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Registra adelantos, retenciones, notas de crédito, clawbacks o bonos que afecten la liquidación del profesional.
      </Typography>

      <Stack spacing={2} sx={{ mb: 3 }}>
        <FormControl fullWidth>
          <TextField
            select
            label="Profesional"
            value={selectedProf}
            onChange={(e) => setSelectedProf(e.target.value)}
          >
            <MenuItem value=""><em>Seleccionar profesional</em></MenuItem>
            {professionals.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.fullName || `${p.nombre || ''} ${p.apellidos || ''}`.trim()}
              </MenuItem>
            ))}
          </TextField>
        </FormControl>

        {selectedProf && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              {editing ? 'Editar ajuste' : 'Nuevo ajuste'}
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <TextField
                select
                label="Tipo"
                value={form.tipo}
                onChange={(e) => setForm((f) => ({ ...f, tipo: e.target.value }))}
                sx={{ minWidth: 200 }}
              >
                {TIPOS.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                type="number"
                label="Monto"
                value={form.monto}
                onChange={(e) => setForm((f) => ({ ...f, monto: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                sx={{ minWidth: 140 }}
              />
              <TextField
                type="date"
                label="Fecha"
                value={form.fecha}
                onChange={(e) => setForm((f) => ({ ...f, fecha: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 140 }}
              />
              <TextField
                label="Descripción"
                value={form.descripcion}
                onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))}
                sx={{ minWidth: 240, flex: 1 }}
              />
              <TextField
                label="Paciente (opcional)"
                value={form.paciente}
                onChange={(e) => setForm((f) => ({ ...f, paciente: e.target.value }))}
                sx={{ minWidth: 180, flex: 1 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.pagoConfirmado}
                    onChange={(e) => setForm((f) => ({ ...f, pagoConfirmado: e.target.checked }))}
                  />
                }
                label="Pagado"
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving || !form.monto}
                >
                  {editing ? 'Guardar' : 'Agregar'}
                </Button>
                {editing && (
                  <Button variant="outlined" onClick={resetForm}>
                    Cancelar
                  </Button>
                )}
              </Box>
            </Stack>
          </Paper>
        )}
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Monto</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>Pagado</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ajustes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No hay ajustes registrados.
                  </TableCell>
                </TableRow>
              )}
              {ajustes.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>{a.fecha}</TableCell>
                  <TableCell>{TIPOS.find((t) => t.value === a.tipo)?.label || a.tipo}</TableCell>
                  <TableCell>{CLP(a.monto)}</TableCell>
                  <TableCell>{a.descripcion}</TableCell>
                  <TableCell>{a.pagoConfirmado ? 'Sí' : 'No'}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => handleEdit(a)}>Editar</Button>
                    <Button size="small" color="error" onClick={() => handleDelete(a.id)}>Eliminar</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
